import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  CheckSquare,
  PaperPlaneTilt,
  MapTrifold,
  Wrench,
  Camera,
  Images,
  MagnifyingGlass,
  CircleNotch,
  CaretLeft,
  LinkSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { todayIso } from "@/pages/mobile/MobileStop";

const NAV_ACTIONS = [
  { id: "sms", label: "Send Quick SMS", sub: "Message a customer", icon: PaperPlaneTilt, tone: "lilac", to: "/mobile/messages" },
  { id: "map", label: "Route Map", sub: "Navigate the day", icon: MapTrifold, tone: "cyan", to: "/mobile/map" },
];

const TONES = {
  lime: "bg-[#c39e6d]/15 text-[#c39e6d] border-[#c39e6d]/40",
  cyan: "bg-[#00F2FE]/15 text-[#00F2FE] border-[#00F2FE]/40",
  lilac: "bg-[#C084FC]/15 text-[#C084FC] border-[#C084FC]/40",
};

const PRIORITIES = ["Normal", "High", "Urgent"];
const emptySvc = { customer_name: "", customer_phone: "", address: "", issue_type: "", priority: "Normal", description: "" };

export default function QuickActionSheet({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mode, setMode] = useState("menu");
  const [saving, setSaving] = useState(false);
  const me = user?.name || user?.email || "";

  // task
  const [title, setTitle] = useState("");
  // service call
  const [issueTypes, setIssueTypes] = useState(["Chip Repair"]);
  const [svc, setSvc] = useState(emptySvc);
  const [svcJob, setSvcJob] = useState(null);
  // photo
  const [job, setJob] = useState(null);
  const camRef = useRef(null);
  const galRef = useRef(null);
  // shared job picker: null | "photo" | "service"
  const [jobPickerFor, setJobPickerFor] = useState(null);
  const [jobQuery, setJobQuery] = useState("");
  const [jobResults, setJobResults] = useState([]);
  const [jobSearching, setJobSearching] = useState(false);

  useEffect(() => {
    if (!open) return;
    api
      .get("/settings/service-issues")
      .then(({ data }) => {
        const list = data.issues || [];
        if (list.length) {
          setIssueTypes(list);
          setSvc((s) => ({ ...s, issue_type: s.issue_type || list[0] }));
        }
      })
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!jobPickerFor || !jobQuery.trim()) {
      setJobResults([]);
      return;
    }
    let cancel = false;
    setJobSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/jobs", { params: { q: jobQuery.trim() } });
        if (!cancel) setJobResults((data || []).slice(0, 8));
      } catch {
        if (!cancel) setJobResults([]);
      } finally {
        if (!cancel) setJobSearching(false);
      }
    }, 300);
    return () => {
      cancel = true;
      clearTimeout(t);
    };
  }, [jobQuery, jobPickerFor]);

  if (!open) return null;

  const close = () => {
    onOpenChange(false);
    setTimeout(() => {
      setMode("menu");
      setTitle("");
      setSvc(emptySvc);
      setSvcJob(null);
      setJob(null);
      setJobPickerFor(null);
      setJobQuery("");
      setJobResults([]);
    }, 250);
  };

  const go = (to) => {
    close();
    navigate(to);
  };

  const openPicker = (forMode) => {
    setJobQuery("");
    setJobResults([]);
    setJobPickerFor(forMode);
  };

  const selectJob = async (j) => {
    if (jobPickerFor === "photo") {
      setJob(j);
    } else {
      setSvcJob(j);
      setSvc((s) => ({
        ...s,
        customer_name: j.customer_name || s.customer_name,
        address: j.address || s.address,
      }));
      if (j.customer_id) {
        try {
          const { data } = await api.get(`/customers/${j.customer_id}`);
          if (data?.phone) setSvc((s) => ({ ...s, customer_phone: data.phone }));
        } catch {
          /* no phone on file */
        }
      }
    }
    setJobPickerFor(null);
    setJobQuery("");
    setJobResults([]);
  };

  const createTask = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await api.post("/tasks", { title: title.trim(), date: todayIso(), assignee: me });
      toast.success("Task added to your day");
      close();
      window.dispatchEvent(new Event("focus"));
    } catch {
      toast.error("Could not add task");
    } finally {
      setSaving(false);
    }
  };

  const createService = async () => {
    if (!svc.customer_name.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/service-calls", {
        customer_name: svc.customer_name.trim(),
        customer_phone: svc.customer_phone.trim(),
        customer_id: svcJob?.customer_id || "",
        job_id: svcJob?.id || null,
        job_code: svcJob?.code || "",
        address: svc.address.trim(),
        issue_type: svc.issue_type || issueTypes[0] || "Chip Repair",
        priority: svc.priority,
        description: svc.description.trim(),
        status: "Open",
      });
      toast.success(`Service call ${data.code} logged`);
      const id = data.id;
      close();
      navigate(`/mobile/service/${id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not log service call");
    } finally {
      setSaving(false);
    }
  };

  const onPhotoPicked = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || !job) return;
    setSaving(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      await api.post(`/jobs/${job.id}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Photo added to ${job.code}`);
      close();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setSaving(false);
    }
  };

  const field =
    "w-full rounded-xl border border-slate-700 bg-[#0B1220] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-[#c39e6d] focus:outline-none";
  const backBtn = (onClick, testid = "fab-back") => (
    <button
      data-testid={testid}
      onClick={onClick}
      className="flex h-9 items-center gap-1 rounded-full border border-slate-700 px-3 text-xs font-bold uppercase tracking-widest text-slate-300 active:scale-95"
    >
      <CaretLeft size={14} weight="bold" /> Back
    </button>
  );

  const jobChip = (j, onChange, testid) => (
    <div
      className="flex items-center gap-3 rounded-xl border border-slate-800 bg-[#0B1220] p-3"
      data-testid={testid}
    >
      <span className="font-mono-data text-xs font-bold text-[#c39e6d]">{j.code}</span>
      <span className="min-w-0 flex-1 truncate text-sm text-slate-200">
        {j.customer_name || j.project_name || "Job"}
      </span>
      <button
        data-testid={`${testid}-change`}
        onClick={onChange}
        className="text-xs font-semibold uppercase tracking-widest text-slate-400"
      >
        Change
      </button>
    </div>
  );

  const titles = {
    menu: "Quick actions",
    task: "New crew task",
    service: "Log service call",
    photo: "Upload job photo",
  };
  const showPicker = jobPickerFor !== null;

  const PrimaryAction = ({ testid, icon: Icon, label, sub, onClick }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-2xl border border-[#c39e6d]/40 bg-[#c39e6d]/10 p-3 text-left transition-transform active:scale-[0.98]"
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#c39e6d]/40 bg-[#c39e6d]/15 text-[#c39e6d]">
        <Icon size={22} weight="bold" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-bold text-slate-100">{label}</span>
        <span className="block text-xs text-slate-400">{sub}</span>
      </span>
    </button>
  );

  return (
    <div
      className="no-print fixed inset-0 z-[60] flex items-end justify-center"
      data-testid="mobile-quick-action-sheet"
    >
      <button
        aria-label="Close"
        data-testid="fab-sheet-backdrop"
        onClick={close}
        className="absolute inset-0 bg-[#080C14]/80 backdrop-blur-sm animate-in fade-in duration-200"
      />
      <div className="relative z-10 mb-[max(1.25rem,env(safe-area-inset-bottom))] max-h-[82vh] w-[calc(100%-1.25rem)] max-w-md overflow-y-auto rounded-3xl border border-slate-700/60 bg-[#0F172A] p-4 shadow-2xl shadow-black/80 animate-in slide-in-from-bottom-4 duration-300">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">
            {showPicker ? "Select a job" : titles[mode]}
          </div>
          <button
            data-testid="fab-sheet-close"
            onClick={close}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-700 text-slate-400 active:scale-90"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {showPicker && (
          <div className="space-y-3">
            <div className="relative">
              <MagnifyingGlass
                size={16}
                weight="bold"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
              />
              <input
                autoFocus
                data-testid="fab-job-search"
                value={jobQuery}
                onChange={(e) => setJobQuery(e.target.value)}
                placeholder="Search a job by code or customer"
                className={`${field} pl-9`}
              />
            </div>
            <div className="max-h-56 space-y-1.5 overflow-y-auto" data-testid="fab-job-results">
              {jobSearching && (
                <div className="flex items-center justify-center py-4 text-slate-500">
                  <CircleNotch size={18} weight="bold" className="animate-spin" />
                </div>
              )}
              {!jobSearching &&
                jobResults.map((j) => (
                  <button
                    key={j.id}
                    data-testid={`fab-job-${j.id}`}
                    onClick={() => selectJob(j)}
                    className="flex w-full items-center gap-3 rounded-xl border border-slate-800 bg-[#0B1220] p-3 text-left active:scale-[0.98]"
                  >
                    <span className="font-mono-data text-xs font-bold text-[#c39e6d]">{j.code}</span>
                    <span className="min-w-0 flex-1 truncate text-sm text-slate-200">
                      {j.customer_name || j.project_name || "Job"}
                    </span>
                  </button>
                ))}
              {!jobSearching && jobQuery.trim() && jobResults.length === 0 && (
                <div className="py-4 text-center text-xs text-slate-500">No jobs found</div>
              )}
            </div>
            <div className="flex">{backBtn(() => setJobPickerFor(null), "fab-job-cancel")}</div>
          </div>
        )}

        {!showPicker && mode === "menu" && (
          <div className="space-y-2">
            <PrimaryAction
              testid="fab-action-create-task"
              icon={CheckSquare}
              label="Create Crew Task"
              sub="Add a to-do to your day"
              onClick={() => setMode("task")}
            />
            <PrimaryAction
              testid="fab-action-service"
              icon={Wrench}
              label="Log Service Call"
              sub="Report a chip, repair or issue"
              onClick={() => setMode("service")}
            />
            <PrimaryAction
              testid="fab-action-photo"
              icon={Camera}
              label="Upload Job Photo"
              sub="Attach a photo to any job"
              onClick={() => setMode("photo")}
            />
            {NAV_ACTIONS.map((a) => (
              <button
                key={a.id}
                data-testid={`fab-action-${a.id}`}
                onClick={() => go(a.to)}
                className="flex w-full items-center gap-3 rounded-2xl border border-slate-800 bg-[#0B1220] p-3 text-left transition-transform active:scale-[0.98]"
              >
                <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border ${TONES[a.tone]}`}>
                  <a.icon size={22} weight="bold" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-bold text-slate-100">{a.label}</span>
                  <span className="block text-xs text-slate-400">{a.sub}</span>
                </span>
              </button>
            ))}
          </div>
        )}

        {!showPicker && mode === "task" && (
          <div className="space-y-3">
            <input
              autoFocus
              data-testid="fab-task-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createTask()}
              placeholder="What needs doing?"
              className={field}
            />
            <div className="flex gap-2">
              {backBtn(() => setMode("menu"))}
              <button
                data-testid="fab-task-save"
                onClick={createTask}
                disabled={saving || !title.trim()}
                className="flex h-9 flex-1 items-center justify-center gap-2 rounded-full bg-[#c39e6d] text-xs font-bold uppercase tracking-widest text-slate-950 transition-transform active:scale-95 disabled:opacity-40"
              >
                {saving ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : "Add task"}
              </button>
            </div>
          </div>
        )}

        {!showPicker && mode === "service" && (
          <div className="space-y-3">
            {svcJob ? (
              jobChip(svcJob, () => setSvcJob(null), "fab-service-job-chip")
            ) : (
              <button
                data-testid="fab-service-link-job"
                onClick={() => openPicker("service")}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#00F2FE]/50 bg-[#00F2FE]/5 py-2.5 text-xs font-bold uppercase tracking-widest text-[#00F2FE] active:scale-[0.98]"
              >
                <LinkSimple size={15} weight="bold" /> Link a job (auto-fills details)
              </button>
            )}
            <input
              data-testid="fab-service-customer"
              value={svc.customer_name}
              onChange={(e) => setSvc({ ...svc, customer_name: e.target.value })}
              placeholder="Customer name"
              className={field}
            />
            <input
              data-testid="fab-service-address"
              value={svc.address}
              onChange={(e) => setSvc({ ...svc, address: e.target.value })}
              placeholder="Address (optional)"
              className={field}
            />
            <input
              data-testid="fab-service-phone"
              type="tel"
              value={svc.customer_phone}
              onChange={(e) => setSvc({ ...svc, customer_phone: e.target.value })}
              placeholder="Customer phone (for service texts)"
              className={field}
            />
            <select
              data-testid="fab-service-issue"
              value={svc.issue_type || issueTypes[0]}
              onChange={(e) => setSvc({ ...svc, issue_type: e.target.value })}
              className={field}
            >
              {issueTypes.map((it) => (
                <option key={it} value={it} className="bg-[#0B1220]">
                  {it}
                </option>
              ))}
            </select>
            <div className="flex gap-2" data-testid="fab-service-priority">
              {PRIORITIES.map((p) => (
                <button
                  key={p}
                  onClick={() => setSvc({ ...svc, priority: p })}
                  className={`h-10 flex-1 rounded-xl border text-xs font-bold uppercase tracking-widest transition-colors ${
                    svc.priority === p
                      ? "border-[#c39e6d] bg-[#c39e6d]/15 text-[#c39e6d]"
                      : "border-slate-700 text-slate-400"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
            <textarea
              data-testid="fab-service-description"
              value={svc.description}
              onChange={(e) => setSvc({ ...svc, description: e.target.value })}
              placeholder="What's the issue? (optional)"
              rows={3}
              className={`${field} resize-none`}
            />
            <div className="flex gap-2">
              {backBtn(() => setMode("menu"))}
              <button
                data-testid="fab-service-save"
                onClick={createService}
                disabled={saving || !svc.customer_name.trim()}
                className="flex h-9 flex-1 items-center justify-center gap-2 rounded-full bg-[#c39e6d] text-xs font-bold uppercase tracking-widest text-slate-950 transition-transform active:scale-95 disabled:opacity-40"
              >
                {saving ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : "Log call"}
              </button>
            </div>
          </div>
        )}

        {!showPicker && mode === "photo" && (
          <div className="space-y-3">
            {!job ? (
              <>
                <button
                  data-testid="fab-photo-pick-job"
                  onClick={() => openPicker("photo")}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#c39e6d]/50 bg-[#c39e6d]/5 py-3 text-xs font-bold uppercase tracking-widest text-[#c39e6d] active:scale-[0.98]"
                >
                  <MagnifyingGlass size={15} weight="bold" /> Choose a job
                </button>
                <div className="flex">{backBtn(() => setMode("menu"))}</div>
              </>
            ) : (
              <>
                {jobChip(job, () => setJob(null), "fab-photo-job-chip")}
                <input
                  ref={camRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  onChange={onPhotoPicked}
                  className="hidden"
                  data-testid="fab-photo-camera-input"
                />
                <input
                  ref={galRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={onPhotoPicked}
                  className="hidden"
                  data-testid="fab-photo-gallery-input"
                />
                <div className="grid grid-cols-2 gap-2">
                  <button
                    data-testid="fab-photo-camera"
                    onClick={() => camRef.current?.click()}
                    disabled={saving}
                    className="flex h-20 flex-col items-center justify-center gap-1.5 rounded-2xl border border-[#c39e6d]/40 bg-[#c39e6d]/10 text-[#c39e6d] transition-transform active:scale-95 disabled:opacity-40"
                  >
                    <Camera size={24} weight="bold" />
                    <span className="text-[11px] font-bold uppercase tracking-widest">Camera</span>
                  </button>
                  <button
                    data-testid="fab-photo-gallery"
                    onClick={() => galRef.current?.click()}
                    disabled={saving}
                    className="flex h-20 flex-col items-center justify-center gap-1.5 rounded-2xl border border-[#00F2FE]/40 bg-[#00F2FE]/10 text-[#00F2FE] transition-transform active:scale-95 disabled:opacity-40"
                  >
                    <Images size={24} weight="bold" />
                    <span className="text-[11px] font-bold uppercase tracking-widest">Gallery</span>
                  </button>
                </div>
                {saving && (
                  <div className="flex items-center justify-center gap-2 py-1 text-xs text-slate-400">
                    <CircleNotch size={14} weight="bold" className="animate-spin" /> Uploading…
                  </div>
                )}
                <div className="flex">{backBtn(() => setMode("menu"))}</div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
